import {
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Fragment } from "react";
import type { AnalyticsSeriesPoint } from "@/features/admin/adminAnalytics.types";
import { AnalyticsEmptyState } from "@/features/admin/components/analytics/AnalyticsEmptyState";

const palette = ["#e8c59b", "#7cc4a1", "#89a7e0", "#d9907a", "#c6a4e8"];

type ChartProps = {
  data: AnalyticsSeriesPoint[];
  emptyMessage: string;
  loading?: boolean;
  series: string[];
};

function hasData(data: AnalyticsSeriesPoint[], series: string[]) {
  return data.some((point) => series.some((key) => typeof point[key] === "number"));
}

export function MetricLineChart({ data, emptyMessage, loading, series }: ChartProps) {
  if (loading) {
    return <div className="min-h-[240px] animate-pulse rounded-[1.25rem] bg-white/8" />;
  }

  if (!hasData(data, series)) {
    return <AnalyticsEmptyState message={emptyMessage} />;
  }

  return (
    <div className="h-[260px] min-w-0">
      <ResponsiveContainer height="100%" width="100%">
        <LineChart data={data}>
          <XAxis dataKey="date" stroke="#9d886d" tick={{ fontSize: 11 }} />
          <YAxis stroke="#9d886d" tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={{ background: "#18120e", border: "1px solid rgba(255,255,255,.12)", color: "#fff7ef" }} />
          {series.map((key, index) => (
            <Line
              key={key}
              connectNulls={false}
              dataKey={key}
              dot={data.length < 32}
              stroke={palette[index % palette.length]}
              strokeWidth={2}
              type="monotone"
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MetricBarChart({ data, emptyMessage, loading, series }: ChartProps) {
  if (loading) {
    return <div className="min-h-[240px] animate-pulse rounded-[1.25rem] bg-white/8" />;
  }

  if (!hasData(data, series)) {
    return <AnalyticsEmptyState message={emptyMessage} />;
  }

  return (
    <div className="h-[260px] min-w-0">
      <ResponsiveContainer height="100%" width="100%">
        <BarChart data={data}>
          <XAxis dataKey="date" stroke="#9d886d" tick={{ fontSize: 11 }} />
          <YAxis stroke="#9d886d" tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={{ background: "#18120e", border: "1px solid rgba(255,255,255,.12)", color: "#fff7ef" }} />
          {series.map((key, index) => (
            <Bar key={key} dataKey={key} fill={palette[index % palette.length]} radius={[5, 5, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MetricDonutChart({ data, emptyMessage, loading, series }: ChartProps) {
  const key = series[0];
  if (loading) {
    return <div className="min-h-[240px] animate-pulse rounded-[1.25rem] bg-white/8" />;
  }

  if (!hasData(data, [key])) {
    return <AnalyticsEmptyState message={emptyMessage} />;
  }

  return (
    <div className="h-[260px] min-w-0">
      <ResponsiveContainer height="100%" width="100%">
        <PieChart>
          <Pie data={data} dataKey={key} innerRadius={58} nameKey="label" outerRadius={92}>
            {data.map((entry, index) => (
              <Cell key={String(entry.label ?? index)} fill={palette[index % palette.length]} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ background: "#18120e", border: "1px solid rgba(255,255,255,.12)", color: "#fff7ef" }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function GaugeBar({ percent }: { percent: number }) {
  const color = percent >= 90 ? "bg-[#d46a5d]" : percent >= 70 ? "bg-[#d6a653]" : "bg-[#66b88a]";
  return (
    <div className="h-3 overflow-hidden rounded-full bg-white/10">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
    </div>
  );
}

export function MetricHeatmap({
  columns,
  emptyMessage,
  loading,
  rows,
}: {
  columns: string[];
  emptyMessage: string;
  loading?: boolean;
  rows: Array<{ label: string; values: number[] }>;
}) {
  if (loading) {
    return <div className="min-h-[220px] animate-pulse rounded-[1.25rem] bg-white/8" />;
  }

  if (rows.length === 0) {
    return <AnalyticsEmptyState message={emptyMessage} />;
  }

  const max = Math.max(1, ...rows.flatMap((row) => row.values));
  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[520px] gap-2" style={{ gridTemplateColumns: `140px repeat(${columns.length}, minmax(64px, 1fr))` }}>
        <div />
        {columns.map((column) => (
          <div key={column} className="text-xs font-semibold uppercase tracking-[0.12em] text-[#b69d7e]">{column}</div>
        ))}
        {rows.map((row) => (
          <Fragment key={row.label}>
            <div key={`${row.label}-label`} className="py-2 text-sm text-[#efe1cf]">{row.label}</div>
            {row.values.map((value, index) => (
              <div
                key={`${row.label}-${columns[index]}`}
                className="rounded-lg px-3 py-2 text-center text-xs font-semibold text-[#fff7ef]"
                style={{ backgroundColor: `rgba(124, 196, 161, ${0.16 + (value / max) * 0.72})` }}
              >
                {value}
              </div>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
